// src/components/layout/Layout.tsx
import React from 'react';
import Header from './Header';
import Footer from './Footer';
import { motion } from 'framer-motion';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-mauve-50 via-mauve-25 to-white">
      {/* Responsive background patterns */}
      <div className="fixed inset-0 bg-pattern -z-10" />
      
      {/* Animated background elements */}
      <motion.div 
        className="fixed top-0 left-0 w-full h-full -z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-gradient-to-r from-mauve-200/20 to-purple-200/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-pink-200/10 to-rose-200/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
      </motion.div>

      <div className="flex-grow flex flex-col">
        <Header />
        <main className="flex-grow w-full">
          {/* Responsive container for all pages */}
          <div className="container-responsive">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full"
            >
              {children}
            </motion.div>
          </div>
        </main>
        <Footer />
      </div>

    

      {/* Scroll to Top Button - Responsive */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-24 right-6 z-40 w-12 h-12 md:w-14 md:h-14 bg-gradient-to-r from-mauve-500 to-mauve-600 rounded-full shadow-xl flex items-center justify-center opacity-0 hover:opacity-100 transition-all duration-300 hover:scale-110 group"
        aria-label="Scroll to top"
      >
        <span className="text-white text-xl group-hover:animate-bounce">↑</span>
      </button>

      {/* Mobile Navigation Helper - Only shows on small screens */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-t border-mauve-200 py-2 px-4 flex justify-between items-center z-30">
        <a
          href="tel:+233594197765"
          className="flex flex-col items-center gap-1 text-mauve-700 hover:text-mauve-500 transition-colors"
        >
          <span className="text-lg"></span>
          <span className="text-xs font-medium">Call Now</span>
        </a>
        <a
          href="https://maps.google.com/?q=Westlands+Blvd,+Hatso,+Accra"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center gap-1 text-mauve-700 hover:text-mauve-500 transition-colors"
        >
          <span className="text-lg"></span>
          <span className="text-xs font-medium">Location</span>
        </a>
        <a
          href="mailto:gtnailedbyaj@gmail.com"
          className="flex flex-col items-center gap-1 text-mauve-700 hover:text-mauve-500 transition-colors"
        >
          <span className="text-lg">✉️</span>
          <span className="text-xs font-medium">Email</span>
        </a>
        <a
          href="/services"
          className="flex flex-col items-center gap-1 text-mauve-700 hover:text-mauve-500 transition-colors"
        >
          <span className="text-lg">💅</span>
          <span className="text-xs font-medium">Services</span>
        </a>
      </div>
    </div>
  );
};

export default Layout;